import React, { useEffect, useRef, useState } from 'react';
import { Box, Paper, Typography } from '@mui/material';

type RouteCalculationOverlayProps = {
  open: boolean;
  variant?: 'planner' | 'directions' | 'stations';
};

const PLANNER_STEPS: string[] = [
  'Plotting your course...',
  'Locating nearby fuel stations...',
  'Comparing partner discounts...',
  'Optimizing the route...',
  'Finalizing directions...',
];

const DIRECTIONS_STEPS: string[] = [
  'Calculating directions...',
  'Finding the fastest path...',
  'Checking nearby fuel stops...',
  'Almost there...',
];

const STATIONS_STEPS: string[] = [
  'Scanning fuel stations along your route...',
  'Fetching current pump prices...',
  'Applying partner discounts...',
  'Calculating savings opportunities...',
  'Almost ready to display stations...',
];

const VARIANT_TITLES: Record<NonNullable<RouteCalculationOverlayProps['variant']>, string> = {
  planner: 'Calculating your route',
  directions: 'Calculating your route',
  stations: 'Finding fuel stations',
};

const RouteCalculationOverlay: React.FC<RouteCalculationOverlayProps> = ({
  open,
  variant = 'directions',
}) => {
  const steps =
    variant === 'planner'
      ? PLANNER_STEPS
      : variant === 'stations'
      ? STATIONS_STEPS
      : DIRECTIONS_STEPS;
  const title = VARIANT_TITLES[variant];
  const [stepIndex, setStepIndex] = useState(0);
  const startedAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (!open) {
      setStepIndex(0);
      startedAtRef.current = null;
      return;
    }
    startedAtRef.current = Date.now();
    setStepIndex(0);
    const interval = setInterval(() => {
      setStepIndex((prev) => (prev + 1) % steps.length);
    }, 1600);
    return () => clearInterval(interval);
  }, [open, steps.length]);

  if (!open) return null;

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 1500,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        bgcolor: 'rgba(15, 32, 64, 0.45)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        animation: 'nsf-rco-fade-in 0.25s ease-out',
        '@keyframes nsf-rco-fade-in': {
          from: { opacity: 0 },
          to: { opacity: 1 },
        },
        px: 2,
      }}
    >
      <Paper
        elevation={10}
        sx={{
          width: '100%',
          maxWidth: 380,
          borderRadius: 3,
          p: { xs: 2.5, sm: 3 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          background: 'linear-gradient(180deg, #ffffff 0%, #f7fbff 100%)',
          border: '1px solid rgba(26, 95, 180, 0.12)',
          boxShadow: '0 20px 60px rgba(15, 32, 64, 0.28)',
          animation: 'nsf-rco-pop 0.3s cubic-bezier(0.2, 1, 0.3, 1)',
          '@keyframes nsf-rco-pop': {
            from: { transform: 'scale(0.92) translateY(8px)', opacity: 0 },
            to: { transform: 'scale(1) translateY(0)', opacity: 1 },
          },
        }}
      >
        <RouteAnimation />

        <Box sx={{ textAlign: 'center', width: '100%' }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              color: '#0d2b56',
              fontSize: { xs: '16px', sm: '17px' },
              letterSpacing: 0.2,
            }}
          >
            {title}
          </Typography>
          <Box
            sx={{
              position: 'relative',
              mt: 0.75,
              height: 22,
              overflow: 'hidden',
            }}
          >
            {steps.map((label, idx) => (
              <Typography
                key={label}
                variant="body2"
                sx={{
                  position: 'absolute',
                  inset: 0,
                  color: '#1A5FB4',
                  fontSize: { xs: '13px', sm: '14px' },
                  fontWeight: 500,
                  opacity: idx === stepIndex ? 1 : 0,
                  transform: idx === stepIndex ? 'translateY(0)' : 'translateY(8px)',
                  transition: 'opacity 0.4s ease, transform 0.4s ease',
                }}
              >
                {label}
              </Typography>
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            width: '100%',
            height: 4,
            borderRadius: 4,
            bgcolor: 'rgba(26, 95, 180, 0.12)',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              width: '40%',
              borderRadius: 4,
              background:
                'linear-gradient(90deg, rgba(26,95,180,0) 0%, #1A5FB4 50%, rgba(26,95,180,0) 100%)',
              animation: 'nsf-rco-bar 1.4s ease-in-out infinite',
              '@keyframes nsf-rco-bar': {
                '0%': { transform: 'translateX(-120%)' },
                '100%': { transform: 'translateX(320%)' },
              },
            }}
          />
        </Box>

        <Typography
          variant="caption"
          sx={{
            color: '#5a6b85',
            fontSize: { xs: '11px', sm: '12px' },
            textAlign: 'center',
            lineHeight: 1.4,
          }}
        >
          This usually takes just a few seconds. Please keep this screen open.
        </Typography>
      </Paper>
    </Box>
  );
};

const RouteAnimation: React.FC = () => {
  return (
    <Box
      sx={{
        position: 'relative',
        width: 220,
        height: 130,
        '@keyframes nsf-rco-dash': {
          from: { strokeDashoffset: 320 },
          to: { strokeDashoffset: 0 },
        },
        '@keyframes nsf-rco-pulse': {
          '0%, 100%': { transform: 'scale(1)', opacity: 1 },
          '50%': { transform: 'scale(1.15)', opacity: 0.85 },
        },
        '@keyframes nsf-rco-pin-drop': {
          '0%': { transform: 'translateY(-14px) scale(0.7)', opacity: 0 },
          '60%': { transform: 'translateY(2px) scale(1.05)', opacity: 1 },
          '100%': { transform: 'translateY(0) scale(1)', opacity: 1 },
        },
        '@keyframes nsf-rco-car': {
          '0%': { offsetDistance: '0%' },
          '100%': { offsetDistance: '100%' },
        },
        '@keyframes nsf-rco-fuel-pop': {
          '0%': { transform: 'scale(0)', opacity: 0 },
          '60%': { transform: 'scale(1.15)', opacity: 1 },
          '100%': { transform: 'scale(1)', opacity: 1 },
        },
      }}
    >
      <Box
        component="svg"
        viewBox="0 0 220 130"
        sx={{ width: '100%', height: '100%', display: 'block', overflow: 'visible' }}
      >
        <defs>
          <linearGradient id="nsf-rco-route" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#1A5FB4" />
            <stop offset="100%" stopColor="#3b86d8" />
          </linearGradient>
        </defs>

        <path
          d="M 20 95 C 60 95, 70 30, 110 35 S 170 100, 200 40"
          fill="none"
          stroke="rgba(26, 95, 180, 0.15)"
          strokeWidth={6}
          strokeLinecap="round"
        />

        <path
          d="M 20 95 C 60 95, 70 30, 110 35 S 170 100, 200 40"
          fill="none"
          stroke="url(#nsf-rco-route)"
          strokeWidth={4}
          strokeLinecap="round"
          strokeDasharray="320"
          style={{
            animation: 'nsf-rco-dash 2.2s ease-in-out infinite',
          }}
        />

        <circle
          cx={20}
          cy={95}
          r={7}
          fill="#1A5FB4"
          style={{
            transformOrigin: '20px 95px',
            animation: 'nsf-rco-pulse 1.6s ease-in-out infinite',
          }}
        />
        <circle cx={20} cy={95} r={3} fill="#ffffff" />

        <g
          style={{
            transformOrigin: '110px 35px',
            transformBox: 'fill-box',
            animation: 'nsf-rco-fuel-pop 0.6s ease-out 0.6s both',
          }}
        >
          <circle cx={110} cy={35} r={9} fill="#ffffff" stroke="#1A5FB4" strokeWidth={2} />
          <path
            d="M 106 31 L 106 40 L 113 40 L 113 33 L 115 35 L 115 38"
            fill="none"
            stroke="#1A5FB4"
            strokeWidth={1.4}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>

        <g
          style={{
            transformOrigin: '200px 40px',
            transformBox: 'fill-box',
            animation: 'nsf-rco-pin-drop 0.6s ease-out 1s both',
          }}
        >
          <path
            d="M 200 22 C 207 22, 212 27, 212 33 C 212 41, 200 54, 200 54 C 200 54, 188 41, 188 33 C 188 27, 193 22, 200 22 Z"
            fill="#1A5FB4"
            stroke="#0d2b56"
            strokeWidth={1}
          />
          <circle cx={200} cy={32} r={3.5} fill="#ffffff" />
        </g>

        <g
          style={{
            offsetPath: "path('M 20 95 C 60 95, 70 30, 110 35 S 170 100, 200 40')",
            offsetRotate: 'auto',
            animation: 'nsf-rco-car 2.2s ease-in-out infinite',
          }}
        >
          <circle r={4.5} fill="#ffffff" stroke="#1A5FB4" strokeWidth={2} />
        </g>
      </Box>
    </Box>
  );
};

type StationsLoadingChipProps = {
  open: boolean;
  message?: string;
  topOffset?: number | string;
};

export const StationsLoadingChip: React.FC<StationsLoadingChipProps> = ({
  open,
  message = 'Loading nearby stations...',
  topOffset = 80,
}) => {
  if (!open) return null;

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        position: 'absolute',
        top: topOffset,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1200,
        pointerEvents: 'none',
        animation: 'nsf-rco-chip-in 0.25s ease-out',
        '@keyframes nsf-rco-chip-in': {
          from: { opacity: 0, transform: 'translate(-50%, -8px)' },
          to: { opacity: 1, transform: 'translate(-50%, 0)' },
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: 1.75,
          py: 0.9,
          borderRadius: 999,
          bgcolor: 'rgba(255, 255, 255, 0.97)',
          border: '1px solid rgba(26, 95, 180, 0.18)',
          boxShadow: '0 6px 20px rgba(15, 32, 64, 0.18)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
          '@keyframes nsf-chip-radar': {
            '0%': { transform: 'scale(0.6)', opacity: 0.9 },
            '100%': { transform: 'scale(2.4)', opacity: 0 },
          },
          '@keyframes nsf-chip-dot': {
            '0%, 100%': { transform: 'scale(1)' },
            '50%': { transform: 'scale(1.15)' },
          },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: 18,
            height: 18,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              border: '2px solid rgba(26, 95, 180, 0.55)',
              animation: 'nsf-chip-radar 1.6s ease-out infinite',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              border: '2px solid rgba(26, 95, 180, 0.45)',
              animation: 'nsf-chip-radar 1.6s ease-out 0.5s infinite',
            }}
          />
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: '#1A5FB4',
              animation: 'nsf-chip-dot 1.2s ease-in-out infinite',
              boxShadow: '0 0 0 2px rgba(255,255,255,0.95)',
            }}
          />
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: '#0d2b56',
            fontWeight: 600,
            fontSize: { xs: '12.5px', sm: '13px' },
            whiteSpace: 'nowrap',
          }}
        >
          {message}
        </Typography>
      </Box>
    </Box>
  );
};

export default RouteCalculationOverlay;
